// Ver (Onur, 22.10.2026): a danışman's hakediş, or Seda's devir, recorded
// from the desk on the pay sheet's card. The big figure is what is due
// and the dark button records it; "Farklı tutar gir" turns the figure
// into the field, for less. More than due is an avans and stays in
// chat; the devir stops at what the drawer can spare — the door
// refuses both, these sheets only say so first. The strip under the
// card is the drawer before and after. Opened from the Kasa drawer's
// row or the danışman's own drawer, and back to it.

import {useEffect, useRef, useState} from "react";
import type {ReactNode} from "react";
import {HOLDER_TR, parseTL, trDative, trNumber} from "../labels";
import {Icon} from "../ui/Icon";
import {useMoney} from "../ui/blind-context";
import {Sheet} from "./Sheets";

export type PayoutFrom = "kasa" | "owner";

// The head: back to where Ver was tapped, ×, and what the sheet records.
function Head({back, title, onBack, onClose}: {
  back: string; title: string; onBack: () => void; onClose: () => void;
}) {
  return (
    <header className="sd-head">
      <div className="sd-who">
        <button className="sd-chip sd-back" onClick={onBack}>
          <Icon name="back" /><span className="sd-ell">{back}</span>
        </button>
        <span className="spacer" />
        <button className="sd-round" aria-label="Kapat" onClick={onClose}><Icon name="x" /></button>
      </div>
      <h2 className="sd-title">{title}</h2>
    </header>
  );
}

// The amount the button records, as on the pay sheet: what is due at
// rest, what the desk types once "Farklı tutar gir" is tapped.
function useAmount(dueTL: number) {
  const {blind} = useMoney();
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState("");
  const typedTL = editing ? parseTL(typed) : null;
  return {
    editing, typed, setTyped, typedTL,
    amount: editing ? typedTL : dueTL,
    unreadable: editing && typed.trim() !== "" && typedTL === null,
    start: () => {
      setTyped(blind ? "" : trNumber(dueTL));
      setEditing(true);
    },
    stop: () => setEditing(false),
  };
}
type Amount = ReturnType<typeof useAmount>;

function AmountCard({a, dueTL, label, note, methods, go, button, strip}: {
  a: Amount; dueTL: number; label: string; note: {text: string; tone?: "bad"};
  methods?: ReactNode; go: (() => void) | null; button: ReactNode;
  strip: [string, number, boolean?][]; // label, figure, below zero
}) {
  const {blind, num} = useMoney();
  // The figure is the field: all of it selected when it opens.
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (a.editing) {
      field.current?.focus();
      field.current?.select();
    }
  }, [a.editing]);
  const figure = a.editing ? a.typed : num(dueTL);
  const placeholder = blind ? num(dueTL) : "0";
  return (
    <div className="pay-card">
      <div className="pay-main">
        <div className="pay-head">
          <small>{a.editing ? "Verilen tutar" : label}</small>
          <button className="pay-edit" onClick={() => (a.editing ? a.stop() : a.start())}>
            <span className={a.editing ? "off" : ""} aria-hidden={a.editing}>Farklı tutar gir</span>
            <span className={a.editing ? "" : "off"} aria-hidden={!a.editing}>Vazgeç</span>
          </button>
        </div>
        <div className="pay-figure-wrap">
          <div className={a.editing ? "pay-figure-row editing" : "pay-figure-row"}>
            <span className="pay-figure" data-value={figure || placeholder}>
              <input ref={field} type="text" inputMode="decimal" size={1} autoComplete="off"
                readOnly={!a.editing} tabIndex={a.editing ? 0 : -1}
                aria-label={a.editing ? "Verilen tutar (TL)" : `${label} (TL)`}
                placeholder={placeholder} value={figure}
                onChange={(e) => a.setTyped(e.target.value)}
                onBlur={() => a.typedTL !== null && a.setTyped(trNumber(a.typedTL))}
                onKeyDown={(e) => {
                  if (e.key === "Escape" && a.editing) {
                    // Back to the due figure; a second Escape closes the sheet.
                    e.stopPropagation();
                    a.stop();
                  } else if (e.key === "Enter") {
                    go?.();
                  }
                }} />
            </span>
            <span className="pay-cur">TL</span>
          </div>
          <p className={note.tone ? `pay-note ${note.tone}` : "pay-note"} aria-live="polite">
            {note.text}
          </p>
        </div>
        {methods}
        <button className="pay-go" disabled={!go} onClick={() => go?.()}>{button}</button>
      </div>
      <div className="pay-strip">
        {strip.map(([l, v, neg]) => (
          <div key={l} className={neg ? "neg" : ""}><span>{l}</span><b>{num(v)}</b></div>
        ))}
      </div>
    </div>
  );
}

// What the line under the figure says while the desk types: a figure
// it cannot read, none yet, or one above the most allowed.
function typingNote(a: Amount, maxTL: number, over: string): {text: string; tone?: "bad"} | null {
  if (!a.editing) return null;
  if (a.unreadable) return {text: "Okunamadı — örnek: 1.250,50", tone: "bad"};
  if (a.amount === null) return {text: "Tutarı yazın."};
  if (a.amount > maxTL) return {text: over, tone: "bad"};
  return null;
}

// ── A danışman's hakediş ──────────────────────────────────

export function PayoutSheet({name, dueTL, kasaTL, items, pending, estimateTL, back, busy,
  running, onBack, onClose, onPay}: {
  name: string;
  dueTL: number; // her payNowTL: the most this sheet records
  kasaTL: number; // the drawer now
  items: string[]; // the seanslar the figure pays for
  pending: string[]; // her seanslar today with no record yet: "Zeynep Güneş (17:30)"
  estimateTL: number; // their share, roughly — not in dueTL yet
  back: string; // where Ver was tapped
  busy: boolean; running: boolean;
  onBack: () => void; onClose: () => void;
  onPay: (amountTL: number, from: PayoutFrom, idempotencyKey: string) => void;
}) {
  const {tl} = useMoney();
  const a = useAmount(dueTL);
  const [from, setFrom] = useState<PayoutFrom>("kasa");
  // One key per sheet and figure: a tap retried after a lost answer is
  // one record; another amount or source is another payout.
  const [key] = useState(() => crypto.randomUUID());
  const amount = a.amount;
  const ok = amount !== null && amount <= dueTL && !a.unreadable;
  const go = ok && !busy ? () => onPay(amount, from, `${key}|${from}|${amount}`) : null;

  const note = typingNote(a, dueTL,
    `En fazla ${tl(dueTL)}; fazlası avanstır, sohbetten girilir.`) ?? (
    !a.editing ? {text: items.length === 1 ? `${items[0]} seansının payı` :
      items.length > 1 ? `${items.length} seansın payı: ${items.join(", ")}` : "Hakedişi"} :
      amount !== null && amount < dueTL ? {text: `Kalan ${tl(dueTL - amount)} hakedişinde durur.`} :
        {text: "Hakedişin tamamı."});
  const button = running ? "Kaydediliyor…" :
    a.unreadable ? "Tutarı düzeltin" :
      amount === null ? "Tutarı yazın" :
        amount > dueTL ? `En fazla ${tl(dueTL)}` :
          <>{tl(amount)} verildi <span>· {HOLDER_TR[from]}</span></>;
  const leftTL = kasaTL - (from === "kasa" ? amount ?? 0 : 0);
  const one = pending.length === 1;

  return (
    <Sheet className="seans-sheet pay-sheet payout-sheet" onClose={onClose}
      head={<Head back={back} title={`${trDative(name)} ödeme`} onBack={onBack} onClose={onClose} />}>
      <AmountCard a={a} dueTL={dueTL} label="Bugün ver" note={note} go={go} button={button}
        methods={
          <div className="pay-methods two" role="group" aria-label="Nereden verildi">
            {(["kasa", "owner"] as const).map((f) => (
              <button key={f} aria-pressed={from === f} disabled={busy} onClick={() => setFrom(f)}>
                {f === "kasa" ? "Kasadan" : "Seda'nın hesabından"}
              </button>
            ))}
          </div>
        }
        strip={[["Hakediş", dueTL], ["Kasada", kasaTL], ["Kalır", leftTL, leftTL < 0]]} />
      {pending.length > 0 && (
        <p className="pay-warn">
          {pending.join(", ")} henüz kaydedilmedi. {one ? "Seanstan" : "Seanslardan"} sonra
          verirseniz {one ? "onun payı" : "payları"} da
          {estimateTL > 0 ? `, yaklaşık ${tl(estimateTL)},` : ""} bu ödemeye girer.
        </p>
      )}
      <p className="pay-foot">
        Bugün verilecekten fazlası verilmez; avans sohbetten girilir. Kaydettikten hemen sonra
        çıkan uyarıda Geri al durur.
      </p>
    </Sheet>
  );
}

// ── Seda's devir ──────────────────────────────────────────

export function DevirSheet({name, safeTL, owedTL, heldTL, kasaTL, back, busy, running, onBack,
  onClose, onGive}: {
  name: string; // Seda
  safeTL: number; // the most the drawer can hand her now
  owedTL: number; // her hakediş: taken first
  heldTL: number; // what stays for the other danışmanlar
  kasaTL: number;
  back: string;
  busy: boolean; running: boolean;
  onBack: () => void; onClose: () => void;
  onGive: (amountTL: number, idempotencyKey: string) => void;
}) {
  const {tl} = useMoney();
  const a = useAmount(safeTL);
  const [key] = useState(() => crypto.randomUUID());
  const amount = a.amount;
  const ok = amount !== null && amount <= safeTL && !a.unreadable;
  const go = ok && !busy ? () => onGive(amount, `${key}|${amount}`) : null;

  const note = typingNote(a, safeTL,
    `En fazla ${tl(safeTL)}; kalanı diğer danışmanların payı.`) ?? (
    !a.editing || amount === null ?
      {text: heldTL > 0 ? `Diğer danışmanlar için ${tl(heldTL)} kasada kalır.` :
        "Kasada başka danışmanın payı yok."} :
      amount <= owedTL ? {text: `Hakedişinden düşer, ${tl(owedTL - amount)} hakedişi kalır.`} :
        owedTL > 0 ? {text: `${tl(owedTL)} hakediş, ${tl(amount - owedTL)} çekiş.`} :
          {text: "Tamamı çekiş olarak yazılır."});
  const button = running ? "Kaydediliyor…" :
    a.unreadable ? "Tutarı düzeltin" :
      amount === null ? "Tutarı yazın" :
        amount > safeTL ? `En fazla ${tl(safeTL)}` :
          `${tl(amount)} ${trDative(name)} verildi`;
  const leftTL = kasaTL - (amount ?? 0);

  return (
    <Sheet className="seans-sheet pay-sheet payout-sheet" onClose={onClose}
      head={<Head back={back} title={`${trDative(name)} devir`} onBack={onBack} onClose={onClose} />}>
      <AmountCard a={a} dueTL={safeTL} label="Kasadan en fazla" note={note} go={go}
        button={button}
        strip={[["Hakedişi", owedTL], ["Kasada", kasaTL], ["Kalır", leftTL, leftTL < 0]]} />
      <p className="pay-foot">
        Önce hakedişinden düşer; aşan kısım kurumdan çekiş olarak yazılır, bölüşümü sistem yapar.
        En fazla tutarın üstü verilmez.
      </p>
    </Sheet>
  );
}
